'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import ChevronDownIcon from '@heroicons/react/24/outline/ChevronDownIcon';
import ChevronUpIcon from '@heroicons/react/24/outline/ChevronUpIcon';

const MIN_PRICE = 0;
const MAX_PRICE = 100000000;
const MIN_GAP = 100000;
const PRICE_STEP = 100000;

const QUICK_RANGES = [
  { label: 'Dưới 5 triệu', min: 0, max: 5000000 },
  { label: '5 - 10 triệu', min: 5000000, max: 10000000 },
  { label: '10 - 20 triệu', min: 10000000, max: 20000000 },
  { label: '20 - 30 triệu', min: 20000000, max: 30000000 },
  { label: 'Trên 30 triệu', min: 30000000, max: MAX_PRICE },
] as const;

function clampPrice(value: number) {
  return Math.min(MAX_PRICE, Math.max(MIN_PRICE, value));
}

function toNumber(input: string) {
  const raw = input.replace(/[^0-9]/g, '');
  return raw ? Number(raw) : 0;
}

export default function PriceFilter() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const queryMin = parseInt(searchParams.get('min_price') || '0', 10);
  const queryMax = parseInt(searchParams.get('max_price') || String(MAX_PRICE), 10);

  const [min, setMin] = useState(queryMin);
  const [max, setMax] = useState(queryMax);
  const [minInput, setMinInput] = useState(String(queryMin));
  const [maxInput, setMaxInput] = useState(String(queryMax));
  const [isOpen, setIsOpen] = useState(true);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const queryString = useMemo(() => searchParams.toString(), [searchParams]);

  useEffect(() => {
    setMin(queryMin);
    setMax(queryMax);
    setMinInput(String(queryMin));
    setMaxInput(String(queryMax));
  }, [queryMin, queryMax]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const commitUrl = (nextMin: number, nextMax: number) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      const next = new URLSearchParams(queryString);
      if (nextMin > 0) next.set('min_price', String(nextMin));
      else next.delete('min_price');

      if (nextMax < MAX_PRICE) next.set('max_price', String(nextMax));
      else next.delete('max_price');

      const current = queryString;
      const target = next.toString();
      if (current === target) return;

      router.replace(`${pathname}?${target}`, { scroll: false });
    }, 180);
  };

  const syncState = (nextMin: number, nextMax: number, push = true) => {
    const safeMin = clampPrice(Math.min(nextMin, nextMax - MIN_GAP));
    const safeMax = clampPrice(Math.max(nextMax, safeMin + MIN_GAP));
    setMin(safeMin);
    setMax(safeMax);
    setMinInput(String(safeMin));
    setMaxInput(String(safeMax));
    if (push) commitUrl(safeMin, safeMax);
  };

  const applyManual = () => {
    const rawMin = clampPrice(toNumber(minInput));
    const rawMax = clampPrice(toNumber(maxInput) || MAX_PRICE);
    syncState(rawMin, rawMax, true);
  };

  const resetPrice = () => syncState(MIN_PRICE, MAX_PRICE, true);

  const formatPrice = (val: number) => `${new Intl.NumberFormat('vi-VN').format(val)}₫`;

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white/90 shadow-sm transition-all duration-300">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between bg-white px-4 py-3.5 transition-colors hover:bg-slate-50"
      >
        <h3 className="text-[15px] font-bold text-slate-800">Mức giá</h3>
        {isOpen ? (
          <ChevronUpIcon className="h-4 w-4 text-slate-500" />
        ) : (
          <ChevronDownIcon className="h-4 w-4 text-slate-500" />
        )}
      </button>

      <div
        className={`overflow-hidden px-4 transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[740px] pb-5 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="mb-3 grid grid-cols-2 gap-2">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-wide text-slate-400">Từ</p>
            <p className="mt-0.5 text-sm font-semibold text-slate-800">{formatPrice(min)}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-wide text-slate-400">Đến</p>
            <p className="mt-0.5 text-sm font-semibold text-slate-800">{formatPrice(max)}</p>
          </div>
        </div>

        <div className="mb-3 space-y-3">
          <label className="block">
            <p className="mb-1 text-xs font-medium text-slate-500">Giá từ</p>
            <input
              type="range"
              min={MIN_PRICE}
              max={MAX_PRICE}
              step={PRICE_STEP}
              value={min}
              onChange={(e) => syncState(Number(e.target.value), max, true)}
              className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-amber-500"
            />
          </label>

          <label className="block">
            <p className="mb-1 text-xs font-medium text-slate-500">Giá đến</p>
            <input
              type="range"
              min={MIN_PRICE}
              max={MAX_PRICE}
              step={PRICE_STEP}
              value={max}
              onChange={(e) => syncState(min, Number(e.target.value), true)}
              className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-red-500"
            />
          </label>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-2">
          <input
            type="text"
            inputMode="numeric"
            value={minInput}
            onChange={(e) => setMinInput(e.target.value)}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-amber-400"
            placeholder="Giá từ"
          />
          <input
            type="text"
            inputMode="numeric"
            value={maxInput}
            onChange={(e) => setMaxInput(e.target.value)}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-red-400"
            placeholder="Giá đến"
          />
        </div>

        <button
          type="button"
          onClick={applyManual}
          className="mb-4 w-full rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
        >
          Áp dụng mức giá
        </button>

        <div className="mb-4 flex flex-wrap gap-2">
          {QUICK_RANGES.map((range) => {
            const active = min === range.min && max === range.max;
            return (
              <button
                key={range.label}
                type="button"
                onClick={() => syncState(range.min, range.max, true)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                {range.label}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={resetPrice}
          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
        >
          Xóa lọc giá
        </button>
      </div>
    </div>
  );
}
